import * as React from "react";

import { cn } from "@perceptron/utils/cn";

import { Button, type ButtonProps } from "./button";
import { Icon, type IconName } from "./icon";

/**
 * A select-style trigger: a left-aligned {@link SelectButtonLabel} and a
 * right-aligned {@link SelectButtonValue} pill (optional icon + current
 * selection). Pure presentation — pair it with a Popover/Dialog/Sheet that owns
 * the open state and renders the actual options.
 *
 * @example
 * <SelectButton onClick={() => setOpen(true)}>
 *   <SelectButtonLabel>Mode</SelectButtonLabel>
 *   <SelectButtonValue icon="pencil-line">Compose</SelectButtonValue>
 * </SelectButton>
 */
const SelectButton = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant = "secondary", children, ...props }, ref) => (
    <Button
      className={cn("h-auto w-full justify-start rounded-md py-0.5 pl-2 pr-0.5 font-normal", className)}
      ref={ref}
      variant={variant}
      {...props}
    >
      <span className="label-2 flex w-full items-center justify-between gap-2">{children}</span>
    </Button>
  ),
);
SelectButton.displayName = "SelectButton";

/** The left-hand label of a {@link SelectButton} (e.g. "Mode", "Output"). */
const SelectButtonLabel = React.forwardRef<HTMLSpanElement, React.HTMLAttributes<HTMLSpanElement>>(
  ({ className, ...props }, ref) => <span className={cn("whitespace-nowrap", className)} ref={ref} {...props} />,
);
SelectButtonLabel.displayName = "SelectButtonLabel";

export interface SelectButtonValueProps extends React.HTMLAttributes<HTMLSpanElement> {
  /** Optional leading icon (must exist in the UI ICON_MAP). */
  icon?: IconName;
}

/**
 * The right-hand value pill of a {@link SelectButton}: an optional leading icon
 * plus the current selection. Override the background via `className`.
 */
const SelectButtonValue = React.forwardRef<HTMLSpanElement, SelectButtonValueProps>(
  ({ className, icon, children, ...props }, ref) => (
    <span
      className={cn(
        "bg-background translate-z-0 flex shrink-0 transform-gpu items-center gap-1 overflow-hidden rounded-[6px] p-1.5",
        className,
      )}
      ref={ref}
      {...props}
    >
      {icon && <Icon className="size-4 shrink-0" name={icon} />}
      <span className="whitespace-nowrap">{children}</span>
    </span>
  ),
);
SelectButtonValue.displayName = "SelectButtonValue";

export { SelectButton, SelectButtonLabel, SelectButtonValue };
